import { useCallback, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Header } from './components/Header';
import { DashboardPage } from './pages/DashboardPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { DocumentDetailPage } from './pages/DocumentDetailPage';
import { AdvancedSearchPage } from './pages/AdvancedSearchPage';
import { SettingsPage } from './pages/SettingsPage';
import { ClassifiersPage, ClassifierDetailPage, ClassifierVersionPage } from './pages/ClassifiersPage';
import { BundlesPage, BundleDetailPage } from './pages/BundlesPage';
import { FolderMappingPage } from './pages/FolderMappingPage';
import { MonitoringPage } from './pages/MonitoringPage';
import { MonitoringUsagePage } from './pages/MonitoringUsagePage';
import { OutlookMonitoringPage } from './pages/OutlookMonitoringPage';
import { UsersPage } from './pages/admin/UsersPage';
import { OrganizationsPage } from './pages/admin/OrganizationsPage';
import { OrgDetailPage } from './pages/admin/OrgDetailPage';
import { ApiKeysPage as AdminApiKeysPage } from './pages/admin/ApiKeysPage';
import { AuditPage } from './pages/admin/AuditPage';
import { LogsPage } from './pages/admin/LogsPage';
import { EnvironmentsPage, EnvironmentDetailPage } from './pages/admin/EnvironmentsPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { LoginModal } from './components/LoginModal';
import { useDashboardStats } from './hooks/useStats';
import { useAuth } from './lib/auth';
import { socketManager } from './lib/socket';

function App() {
  const { user, loading, refresh, signOut } = useAuth();
  const isLoggedIn = !!user;
  const isAdmin = !!user?.isAlphyAdmin;

  // Get minimal stats for header (only after auth — these calls require it)
  const { health, isConnected, refreshAll } = useDashboardStats(null);

  const handleAuthSuccess = useCallback(async () => {
    await refresh();
    socketManager.reconnect();
    refreshAll();
  }, [refresh, refreshAll]);

  const handleLogout = useCallback(async () => {
    await signOut();
    socketManager.disconnect();
  }, [signOut]);

  // Update page title based on route
  const location = useLocation();
  useEffect(() => {
    const base = 'HarmCheck Console';
    const titles: Record<string, string> = {
      '/': 'Dashboard',
      '/documents': 'Documents',
      '/search': 'Advanced Search',
      '/settings': 'Settings',
      '/classifiers': 'Classifiers',
      '/bundles': 'Bundles',
      '/folder-mapping': 'Folder Mapping',
      '/monitoring': 'Monitoring',
      '/monitoring/usage': 'API Usage',
      '/monitoring/outlook': 'Outlook Monitoring',
      '/admin/users': 'Users',
      '/admin/orgs': 'Organizations',
      '/admin/api-keys': 'API Keys',
      '/admin/audit': 'Audit',
      '/admin/logs': 'Logs',
      '/admin/environments': 'Environments',
    };

    const path = location.pathname;
    const exact = titles[path];
    if (exact) {
      document.title = `${exact} - ${base}`;
    } else if (path.startsWith('/documents/')) {
      document.title = `Document - ${base}`;
    } else if (path.startsWith('/classifiers/')) {
      document.title = `Classifier - ${base}`;
    } else if (path.startsWith('/bundles/')) {
      document.title = `Bundle - ${base}`;
    } else if (path.startsWith('/admin/orgs/')) {
      document.title = `Organization - ${base}`;
    } else if (path.startsWith('/admin/environments/')) {
      document.title = `Environment - ${base}`;
    } else {
      document.title = base;
    }
  }, [location.pathname]);

  // OAuth callback bounces here from the API server. Render its own page so
  // the user doesn't see the login modal flash while we re-fetch /auth/me.
  const isOAuthCallback = location.pathname === '/auth/callback';

  if (loading && !isOAuthCallback) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-gray-500 text-sm">Loading…</div>
      </div>
    );
  }

  if (isOAuthCallback) {
    return (
      <div className="min-h-screen bg-gray-950">
        <Routes>
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
        </Routes>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-950">
        <LoginModal isOpen={true} onSuccess={handleAuthSuccess} />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-6">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-2xl font-semibold text-gray-100">Access denied</h1>
          <p className="text-gray-400 text-sm">
            The HarmCheck Console is restricted to Alphy staff. Your account ({user.email})
            is signed in but does not have admin access. Ask an existing admin to grant
            you access, or sign out and use a different account.
          </p>
          <button
            type="button"
            onClick={handleLogout}
            className="mt-4 px-4 py-2 rounded bg-gray-800 hover:bg-gray-700 text-gray-100 text-sm font-medium border border-gray-700"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950">
      <Header
        health={health ?? null}
        isConnected={isConnected}
        user={user}
        onLogout={handleLogout}
      />

      <main className="p-6">
        <Routes>
          <Route path="/" element={<DashboardPage refreshAll={refreshAll} />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/documents/:documentId" element={<DocumentDetailPage />} />
          <Route path="/search" element={<AdvancedSearchPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/classifiers" element={<ClassifiersPage />} />
          <Route path="/classifiers/:key" element={<ClassifierDetailPage />} />
          <Route path="/classifiers/:key/:version" element={<ClassifierVersionPage />} />
          <Route path="/bundles" element={<BundlesPage />} />
          <Route path="/bundles/:key" element={<BundleDetailPage />} />
          <Route path="/folder-mapping" element={<FolderMappingPage />} />
          <Route path="/monitoring" element={<MonitoringPage />} />
          <Route path="/monitoring/usage" element={<MonitoringUsagePage />} />
          <Route path="/monitoring/outlook" element={<OutlookMonitoringPage />} />
          <Route path="/admin/users" element={<UsersPage />} />
          <Route path="/admin/orgs" element={<OrganizationsPage />} />
          <Route path="/admin/orgs/:id" element={<OrgDetailPage />} />
          <Route path="/admin/api-keys" element={<AdminApiKeysPage />} />
          <Route path="/admin/audit" element={<AuditPage />} />
          <Route path="/admin/logs" element={<LogsPage />} />
          <Route path="/admin/environments" element={<EnvironmentsPage />} />
          <Route path="/admin/environments/:key" element={<EnvironmentDetailPage />} />
          <Route path="/admin/environments/:key/:tab" element={<EnvironmentDetailPage />} />
        </Routes>
      </main>
    </div>
  );
}

export default App;
